// ScraperInterface.jsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { apiService } from '../services/apiService';
import { 
  Search, X, AlertCircle, Server, 
  Download, Database, Settings, RefreshCw,
  Plus, Filter, ChevronDown, ExternalLink,
  Trash, Edit, Copy, Save
} from 'lucide-react';

const ScraperInterface = () => {
  // Core State Management
  const [searchTerms, setSearchTerms] = useState(['VP Operations', 'Chief Operating Officer']);
  const [locations, setLocations] = useState(['New York', 'Chicago']);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState({
    profilesFound: 0,
    pagesScraped: 0,
    successfulRequests: 0,
    failedRequests: 0,
    dbProfiles: 0,
    lastUpdate: null
  });
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  
  // UI State
  const [activeTab, setActiveTab] = useState('search');
  const [showSettings, setShowSettings] = useState(false);
  const [serverStatus, setServerStatus] = useState({
    available: true,
    dbConnected: false,
    lastCheck: null
  });
  
  // Search and Filter State
  const [newTerm, setNewTerm] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [filters, setFilters] = useState({
    searchQuery: '',
    locationFilter: '',
    dateRange: 'all',
    profileType: 'all',
    sortBy: 'date',
    sortOrder: 'desc'
  });
  
  // Results View State
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [resultsView, setResultsView] = useState('grid'); // 'grid' or 'table'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  
  // Export State
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportOptions, setExportOptions] = useState({
    includeMetadata: true,
    includeStats: true,
    formatDates: true
  });

  // Toast State
  const [toasts, setToasts] = useState([]);
  
  // Refs for intervals
  const pollingInterval = useRef(null);
  const serverCheckInterval = useRef(null);
  const searchDebounceTimeout = useRef(null);

  // Toast Management
  const showToast = useCallback((type, message, duration = 5000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, duration);
  }, []);

  // Server Status Check
  const checkServerStatus = useCallback(async () => {
    try {
      const status = await apiService.checkServerStatus();
      setServerStatus(prev => ({
        ...prev,
        available: status.available,
        dbConnected: status.dbConnected,
        lastCheck: new Date().toISOString()
      }));

      if (!status.available && isRunning) {
        setError('Lost connection to server');
        setIsRunning(false);
        showToast('error', 'Server connection lost');
        stopScraping();
      }
    } catch (error) {
      console.error('Server check failed:', error);
      setServerStatus(prev => ({
        ...prev,
        available: false,
        lastCheck: new Date().toISOString()
      }));
    }
  }, [isRunning, showToast]);

  // Initialize Server Check
  useEffect(() => {
    checkServerStatus();
    serverCheckInterval.current = setInterval(checkServerStatus, 30000);

    return () => {
      if (serverCheckInterval.current) {
        clearInterval(serverCheckInterval.current);
      }
    };
  }, [checkServerStatus]);

  // Auto-load results when Results tab is opened
  useEffect(() => {
    if (activeTab === 'results' && results.length === 0) {
      fetchFinalResults();
    }
  }, [activeTab]);

  // Core Scraping Functions
  const startScraping = useCallback(async () => {
    if (!serverStatus.available) {
      showToast('error', 'Server is not available');
      return;
    }

    if (searchTerms.length === 0 || locations.length === 0) {
      showToast('error', 'Please add at least one search term and location');
      return;
    }

    try {
      setIsRunning(true);
      setProgress(0);
      setError('');
      showToast('info', 'Starting scraping process...');

      const response = await apiService.startScraping(searchTerms, locations);

      if (response.status === 'success') {
        // Start polling for updates
        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
        }

        pollingInterval.current = setInterval(async () => {
          try {
            const stats = await apiService.getStats();
            
            setProgress(stats.progress);
            setStats(prev => ({
              ...prev,
              profilesFound: stats.profiles_found,
              pagesScraped: stats.pages_scraped,
              successfulRequests: stats.successful_requests,
              failedRequests: stats.failed_requests,
              dbProfiles: stats.profiles_in_db || 0,
              lastUpdate: new Date().toISOString()
            }));

            if (!stats.is_running) {
              await handleScrapingComplete();
            }

            if (stats.error) {
              handleScrapingError(new Error(stats.error));
            }
          } catch (error) {
            handleScrapingError(error);
          }
        }, 1000);
      }
    } catch (error) {
      handleScrapingError(error);
    }
  }, [searchTerms, locations, serverStatus.available, showToast]);

  const stopScraping = useCallback(async () => {
    try {
      await apiService.stopScraping();
      setIsRunning(false);
      setError('Scraping stopped by user');
      showToast('info', 'Scraping stopped by user');

      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }

      await fetchFinalResults();
    } catch (error) {
      console.error('Error stopping scraper:', error);
      setError(error.message);
      showToast('error', `Failed to stop scraping: ${error.message}`);
    }
  }, [showToast]);

  const handleScrapingComplete = async () => {
    clearInterval(pollingInterval.current);
    setIsRunning(false);
    showToast('success', 'Scraping completed successfully');
    await fetchFinalResults();
    
    // Update stats one final time
    try {
      const finalStats = await apiService.getStats();
      setStats(prev => ({
        ...prev,
        profilesFound: finalStats.profiles_found,
        pagesScraped: finalStats.pages_scraped,
        successfulRequests: finalStats.successful_requests,
        failedRequests: finalStats.failed_requests,
        dbProfiles: finalStats.profiles_in_db || 0,
        lastUpdate: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error fetching final stats:', error);
    }
  };

  const handleScrapingError = (error) => {
    console.error('Scraping error:', error);
    setError(error.message);
    setIsRunning(false);
    showToast('error', `Scraping failed: ${error.message}`);
    
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
    }
  };

  // Results Management
  const fetchFinalResults = async () => {
    try {
      const response = await apiService.getResults();
      if (response?.profiles) {
        const profiles = Array.isArray(response.profiles) ? response.profiles : [];
        setResults(profiles);
        setTotalPages(Math.ceil(profiles.length / pageSize));
        showToast('success', `Retrieved ${response.profiles.length} profiles`);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      showToast('error', 'Failed to fetch results');
    }
  };

  const handleResultsSearch = useCallback((query) => {
    if (searchDebounceTimeout.current) {
      clearTimeout(searchDebounceTimeout.current);
    }

    searchDebounceTimeout.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, searchQuery: query }));
      setPage(1); // Reset to first page when searching
    }, 300);
  }, []);

  const getFilteredResults = useCallback(() => {
    let filtered = Array.isArray(results) ? [...results] : [];

    // Apply search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(profile => 
        profile.name?.toLowerCase().includes(query) ||
        profile.email?.toLowerCase().includes(query) ||
        profile.company?.toLowerCase().includes(query)
      );
    }

    // Apply location filter
    if (filters.locationFilter) {
      const locationQuery = filters.locationFilter.toLowerCase();
      filtered = filtered.filter(profile =>
        profile.location?.toLowerCase().includes(locationQuery)
      );
    }

    // Apply date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      switch (filters.dateRange) {
        case 'today':
          cutoff.setDate(now.getDate() - 1);
          break;
        case 'week':
          cutoff.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoff.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }
      filtered = filtered.filter(profile => 
        new Date(profile.extracted_at) > cutoff
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[filters.sortBy] || '';
      const bValue = b[filters.sortBy] || '';
      
      if (filters.sortOrder === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    return filtered;
  }, [results, filters]);

  // Export Functions
  const handleExport = async () => {
    try {
      setIsExporting(true);
      showToast('info', `Preparing ${exportFormat.toUpperCase()} export...`);

      const data = await apiService.exportResults(exportFormat, exportOptions);
      
      // Create and trigger download
      const blob = new Blob(
        [data], 
        { type: exportFormat === 'csv' ? 'text/csv' : 'application/json' }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scraping_results_${new Date().toISOString()}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showToast('success', `Successfully exported results as ${exportFormat.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      showToast('error', `Failed to export results: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Search Terms Management
  const addSearchTerm = useCallback(() => {
    if (newTerm && !searchTerms.includes(newTerm)) {
      setSearchTerms(prev => [...prev, newTerm.trim()]);
      setNewTerm('');
      showToast('success', `Added search term: ${newTerm}`);
    }
  }, [newTerm, searchTerms, showToast]);

  const removeSearchTerm = useCallback((term) => {
    setSearchTerms(prev => prev.filter(t => t !== term));
    showToast('info', `Removed search term: ${term}`);
  }, [showToast]);

  // Location Management
  const addLocation = useCallback(() => {
    if (newLocation && !locations.includes(newLocation)) {
      setLocations(prev => [...prev, newLocation.trim()]);
      setNewLocation('');
      showToast('success', `Added location: ${newLocation}`);
    }
  }, [newLocation, locations, showToast]);

  const removeLocation = useCallback((location) => {
    setLocations(prev => prev.filter(l => l !== location));
    showToast('info', `Removed location: ${location}`);
  }, [showToast]);

  // Pagination Functions
  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
    setTotalPages(Math.ceil(results.length / newSize));
  };

  // Profile Management
  const handleProfileSelect = (profile) => {
    setSelectedProfile(profile);
  };

  const handleProfileAction = async (action, profile) => {
    switch (action) {
      case 'delete':
        try {
          await apiService.deleteProfile(profile.email);
          setResults(prev => prev.filter(p => p.email !== profile.email));
          showToast('success', 'Profile deleted successfully');
        } catch (error) {
          showToast('error', `Failed to delete profile: ${error.message}`);
        }
        break;
      
      case 'export':
        try {
          const data = JSON.stringify(profile, null, 2);
          const blob = new Blob([data], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `profile_${profile.email}.json`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          showToast('success', 'Profile exported successfully');
        } catch (error) {
          showToast('error', `Failed to export profile: ${error.message}`);
        }
        break;
      
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
              <Search className="h-8 w-8 text-blue-500" />
                <h1 className="text-2xl font-bold text-gray-900">wnleads Scraper</h1>
              </div>
              <div className="flex items-center gap-4">
                {/* Server Status */}
                <div className="flex items-center gap-2">
                  <Server 
                    className={`h-5 w-5 ${serverStatus.available ? 'text-green-500' : 'text-red-500'}`}
                  />
                  <span className={`text-sm font-medium ${
                    serverStatus.available ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {serverStatus.available ? 'Server Connected' : 'Server Disconnected'}
                  </span>
                  {serverStatus.dbConnected && (
                    <Database className="h-5 w-5 text-blue-500 ml-2" />
                  )}
                </div>
                
                {/* Settings Button */}
                <button
                  onClick={() => setShowSettings(prev => !prev)}
                  className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                  title="Settings"
                >
                  <Settings className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-6">
            {/* Tabs */}
            <div className="flex space-x-4 mb-6">
              <TabButton
                active={activeTab === 'search'}
                onClick={() => setActiveTab('search')}
              >
                Search
              </TabButton>
              <TabButton
                active={activeTab === 'results'}
                onClick={() => setActiveTab('results')}
              >
                Results ({stats.profilesFound})
              </TabButton>
              <TabButton
                active={activeTab === 'stats'}
                onClick={() => setActiveTab('stats')}
              >
                Statistics
              </TabButton>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
              {/* Search Tab */}
              {activeTab === 'search' && (
                <div className="space-y-8">
                  {/* Search Terms Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Search Terms</h3>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        placeholder="Add search term..."
                        value={newTerm}
                        onChange={(e) => setNewTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addSearchTerm()}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={addSearchTerm}
                        className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {searchTerms.map(term => (
                        <span
                          key={term}
                          className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full flex items-center gap-1.5 border border-blue-200"
                        >
                          {term}
                          <X 
                            className="h-4 w-4 cursor-pointer hover:text-red-500"
                            onClick={() => removeSearchTerm(term)}
                          />
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Locations Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Locations</h3>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        placeholder="Add location..."
                        value={newLocation}
                        onChange={(e) => setNewLocation(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addLocation()}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={addLocation}
                        className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {locations.map(location => (
                        <span
                          key={location}
                          className="px-3 py-1.5 bg-green-50 text-green-700 rounded-full flex items-center gap-1.5 border border-green-200"
                        >
                          {location}
                          <X 
                            className="h-4 w-4 cursor-pointer hover:text-red-500"
                            onClick={() => removeLocation(location)}
                          />
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Control Buttons */}
                  <div className="flex gap-4 pt-4">
                    {!serverStatus.available ? (
                      <div className="w-full p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-center">
                        Server is not available
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={startScraping}
                          disabled={isRunning || searchTerms.length === 0 || locations.length === 0}
                          className="flex-1 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 
                                   transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                        >
                          {isRunning ? 'Scraping...' : 'Start Scraping'}
                        </button>
                        <button
                          onClick={stopScraping}
                          disabled={!isRunning}
                          className="flex-1 px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 
                                   transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                        >
                          Stop
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Force Stop / Rescue Action */}
                  <div className="flex justify-end pt-2">
                     <button
                        onClick={stopScraping}
                        className="text-xs text-red-500 hover:text-red-700 underline flex items-center gap-1"
                        title="Use this if the scraper gets stuck in 'Running' state"
                      >
                        <AlertCircle className="h-3 w-3" />
                        Force Stop Scraper
                      </button>
                  </div>

                  {/* Progress Bar */}
                  {isRunning && (
                    <div className="space-y-2">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <div className="text-sm text-gray-600 text-right">
                        {Math.round(progress)}% Complete
                      </div>
                    </div>
                  )}

                  {/* Error Display */}
                  {error && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-3">
                      <AlertCircle className="h-5 w-5" />
                      <span>{error}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Results Tab */}
              {activeTab === 'results' && (
                <div className="space-y-6">
                  {/* Results Controls */}
                  <div className="flex justify-between items-center">
                    <div className="flex gap-4">
                      <input
                        type="text"
                        placeholder="Search results..."
                        value={filters.searchQuery}
                        onChange={(e) => handleResultsSearch(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <select
                        value={filters.dateRange}
                        onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="week">Past Week</option>
                        <option value="month">Past Month</option>
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={fetchFinalResults}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600
                                 transition-colors flex items-center gap-2"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Load Results
                      </button>
                      <button
                        onClick={() => setResultsView('grid')}
                        className={`p-2 rounded ${resultsView === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'}`}
                      >
                        Grid
                      </button>
                      <button
                        onClick={() => setResultsView('table')}
                        className={`p-2 rounded ${resultsView === 'table' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'}`}
                      >
                        Table
                      </button>
                      <button
                        onClick={() => handleExport()}
                        disabled={isExporting || results.length === 0}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600
                                 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {isExporting ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        Export
                      </button>
                    </div>
                  </div>

                  {/* Results Display */}
                  {results.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      No results found
                    </div>
                  ) : resultsView === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {getFilteredResults()
                        .slice((page - 1) * pageSize, page * pageSize)
                        .map(profile => (
                          <ProfileCard
                            key={profile.email}
                            profile={profile}
                            onSelect={handleProfileSelect}
                            onAction={handleProfileAction}
                          />
                        ))}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {['Name', 'Email', 'Company', 'Location', 'Actions'].map(header => (
                              <th
                                key={header}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getFilteredResults()
                            .slice((page - 1) * pageSize, page * pageSize)
                            .map(profile => (
                              <tr key={profile.email}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {profile.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {profile.email}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {profile.company}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {profile.location}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleProfileAction('export', profile)}
                                      className="p-1 text-gray-400 hover:text-gray-600"
                                    >
                                      <Download className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleProfileSelect(profile)}
                                      className="p-1 text-gray-400 hover:text-gray-600"
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleProfileAction('delete', profile)}
                                      className="p-1 text-gray-400 hover:text-red-600"
                                    >
                                      <Trash className="h-4 w-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination */}
                  <div className="flex justify-between items-center mt-6">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        Show
                      </span>
                      <select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        className="px-2 py-1 border border-gray-300 rounded"
                      >
                        {[10, 20, 50, 100].map(size => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                      <span className="text-sm text-gray-600">
                        entries
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={page === 1}
                        className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                      >
                        First
                      </button>
                      <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                        className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                      >
                        Next
                      </button>
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={page === totalPages}
                        className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                      >
                        Last
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats Tab */}
              {activeTab === 'stats' && (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard
                      title="Total Profiles"
                      value={stats.profilesFound}
                      icon={<Database className="h-6 w-6" />}
                      color="blue"
                    />
                    <StatCard
                      title="Pages Scraped"
                      value={stats.pagesScraped}
                      icon={<Search className="h-6 w-6" />}
                      color="green"
                    />
                    <StatCard
                      title="Success Rate"
                      value={`${Math.round((stats.successfulRequests / (stats.successfulRequests + stats.failedRequests)) * 100 || 0)}%`}
                      icon={<RefreshCw className="h-6 w-6" />}
                      color="purple"
                    />
                    <StatCard
                      title="Database Profiles"
                      value={stats.dbProfiles}
                      icon={<Database className="h-6 w-6" />}
                      color="yellow"
                    />
                  </div>

                  {/* Additional Stats */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4">Detailed Statistics</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Successful Requests</span>
                        <span className="font-medium">{stats.successfulRequests}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Failed Requests</span>
                        <span className="font-medium">{stats.failedRequests}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Last Update</span>
                        <span className="font-medium">
                          {stats.lastUpdate ? new Date(stats.lastUpdate).toLocaleString() : 'Never'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-lg w-full m-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Settings</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              {/* Export Settings */}
              <div>
                <h3 className="font-medium mb-2">Export Options</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeMetadata}
                      onChange={(e) => setExportOptions(prev => ({
                        ...prev,
                        includeMetadata: e.target.checked
                      }))}
                      className="rounded border-gray-300"
                    />
                    Include Metadata
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeStats}
                      onChange={(e) => setExportOptions(prev => ({
                        ...prev,
                        includeStats: e.target.checked
                      }))}
                      className="rounded border-gray-300"
                    />
                    Include Statistics
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={exportOptions.formatDates}
                      onChange={(e) => setExportOptions(prev => ({
                        ...prev,
                        formatDates: e.target.checked
                      }))}
                      className="rounded border-gray-300"
                    />
                    Format Dates
                  </label>
                </div>
              </div>

              {/* Export Format */}
              <div>
                <h3 className="font-medium mb-2">Export Format</h3>
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="csv">CSV</option>
                  <option value="json">JSON</option>
                  <option value="xlsx">Excel</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Messages */}
      <div className="fixed bottom-4 right-4 space-y-2">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`p-4 rounded-lg shadow-lg text-white ${
              toast.type === 'error' ? 'bg-red-500' :
              toast.type === 'success' ? 'bg-green-500' :
              'bg-blue-500'
            }`}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper Components
const TabButton = ({ children, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
      active
        ? 'bg-blue-500 text-white shadow-md'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
  >
    {children}
  </button>
);

const STAT_COLORS = {
  blue:   { bg: 'rgba(59,130,246,0.08)',   border: 'rgba(59,130,246,0.2)',   text: '#60a5fa' },
  green:  { bg: 'rgba(0,200,150,0.08)',    border: 'rgba(0,200,150,0.2)',    text: '#00C896' },
  purple: { bg: 'rgba(139,92,246,0.08)',   border: 'rgba(139,92,246,0.2)',   text: '#a78bfa' },
  yellow: { bg: 'rgba(255,184,48,0.08)',   border: 'rgba(255,184,48,0.2)',   text: '#FFB830' },
};

const StatCard = ({ title, value, icon, color }) => {
  const c = STAT_COLORS[color] || STAT_COLORS.blue;
  return (
    <div className="rounded-lg p-6" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
      <div className="flex items-center gap-4">
        <div style={{ color: c.text }}>{icon}</div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-2xl font-bold" style={{ color: c.text }}>{value}</p>
        </div>
      </div>
    </div>
  );
};

const ProfileCard = ({ profile, onSelect, onAction }) => (
  <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
    <div className="flex justify-between items-start">
      <div>
        <h3 className="font-medium text-gray-900">{profile.name}</h3>
        <p className="text-sm text-gray-500">{profile.email}</p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onAction('export', profile)}
          className="p-1 text-gray-400 hover:text-gray-600"
        >
          <Download className="h-4 w-4" />
        </button>
        <button
          onClick={() => onSelect(profile)}
          className="p-1 text-gray-400 hover:text-gray-600"
        >
          <ExternalLink className="h-4 w-4" />
        </button>
      </div>
    </div>
    <div className="mt-4">
      <p className="text-sm text-gray-600">
        <span className="font-medium">Company:</span> {profile.company}
      </p>
      <p className="text-sm text-gray-600">
        <span className="font-medium">Location:</span> {profile.location}
      </p>
    </div>
  </div>
);

export default ScraperInterface;